import {
  SpecialRequestItem,
  SpecialRequestType,
  SpecialRequestsRoom,
  SpecialRequestsRoomIn
} from '@appTypes/api.types';

/**
 * Booking special requests - the shared draft and summary rules (QA doc docs features/booking-special-requests/qa.md R-SR6, R-SR8 ..
 * R-SR11, R-SR14, R-SR19c, R-SR19e, R-SR19f / R-SR19g). Pure functions; the components and services call them.
 */

/** A booking number at most 40 (R-SR10). The Other wishes limit (200, R-SR8) lives in the back-office and the server since SRDIALOG-001. */
export const BOOKING_NUMBER_MAX = 40;

export type SpecialRequestsLinkKind = 'none' | 'room' | 'booking';

/** What the agent set for one room, before or after it was saved. */
export interface SpecialRequestsDraft {
  typeIds: number[];
  otherWishes: string;
  linkKind: SpecialRequestsLinkKind;
  /** the key of the linked room: the checkout form index at checkout, the room id on the reservation page */
  linkRoomKey: string | null;
  bookingNumber: string;
}

/** One room the agent may link to (the other rooms of the same cart / reservation, R-SR9). */
export interface SpecialRequestsOtherRoom {
  key: string;
  roomNo: number;
  label: string;
}

export const emptyDraft = (): SpecialRequestsDraft => ({
  typeIds: [],
  otherWishes: '',
  linkKind: 'none',
  linkRoomKey: null,
  bookingNumber: ''
});

/** R-SR6 / R-SR11: a room "has something" when an item is ticked, a wish is written or a link is set. */
export const draftHasContent = (d: SpecialRequestsDraft | null | undefined): boolean =>
  !!d &&
  (d.typeIds.length > 0 ||
    d.otherWishes.trim().length > 0 ||
    (d.linkKind === 'room' && !!d.linkRoomKey) ||
    (d.linkKind === 'booking' && d.bookingNumber.trim().length > 0));

/** Whole nights between two ISO dates (yyyy-MM-dd...), null when one is missing or the order is wrong. */
export const nightsBetween = (from: string | null | undefined, to: string | null | undefined): number | null => {
  if (!from || !to) return null;
  const a = Date.parse(from.slice(0, 10));
  const b = Date.parse(to.slice(0, 10));
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return null;
  return Math.round((b - a) / 86_400_000);
};

/** The item name in the interface language (R-SR6): Russian on the Russian site, English otherwise. */
export const itemName = (
  item: Pick<SpecialRequestType, 'nameEn' | 'nameRu'>,
  lang: string | null | undefined
): string => ((lang ?? '').toLowerCase().startsWith('ru') ? item.nameRu || item.nameEn : item.nameEn || item.nameRu);

/** R-SR9 / R-SR11: «items · wishes · link», joined by a middle dot; an empty part is left out. */
export const summarize = (names: string[], wishes: string | null | undefined, linkText: string | null): string =>
  [...names, ...(wishes?.trim() ? [`«${wishes.trim()}»`] : []), ...(linkText ? [linkText] : [])].join(' · ');

/**
 * R-SR10: the screen checks only the shape before a call - an empty number, and on the reservation page the number of THIS reservation.
 * Everything else (unknown, cancelled, another agency's) is the server's decision.
 */
export const bookingNumberRefused = (value: string, ownNumber?: string | null): boolean => {
  const v = value.trim();
  return v.length === 0 || (!!ownNumber && v === String(ownNumber).trim());
};

/** A saved room -> the popup draft (reservation page). A retired item stays ticked so the agent can untick it (R-SR19g). */
export const draftFromRoom = (room: SpecialRequestsRoom): SpecialRequestsDraft => ({
  typeIds: room.items.map((i) => i.typeId),
  otherWishes: room.otherWishes ?? '',
  linkKind: room.link?.kind ?? 'none',
  linkRoomKey: room.link?.kind === 'room' ? room.link.roomId : null,
  bookingNumber: room.link?.kind === 'booking' ? room.link.bookingNumber : ''
});

/** The popup draft -> the reservation-page PUT body (R-SR14, R-SR19c: the row version read last goes back). */
const roomInLink = (d: SpecialRequestsDraft): SpecialRequestsRoomIn['link'] => {
  if (d.linkKind === 'room' && d.linkRoomKey) return { kind: 'room', roomId: d.linkRoomKey };
  if (d.linkKind === 'booking') return { kind: 'booking', bookingNumber: d.bookingNumber.trim() };
  return null;
};

export const toRoomIn = (d: SpecialRequestsDraft, rowVersion: string | null | undefined): SpecialRequestsRoomIn => ({
  typeIds: [...d.typeIds],
  otherWishes: d.otherWishes.trim() || null,
  link: roomInLink(d),
  rowVersion: rowVersion ?? null
});

/** A translate function as `TranslateService.instant` gives it - keeps this file free of Angular. */
export type SpecialRequestsTranslate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The summary of a SAVED room (R-SR9, R-SR11, R-SR19e): item names in the UI language, «wishes», then "linked to Room n" /
 * "linked to reservation n". A room link whose room was removed (`linkedRoomNo` null) adds nothing here - the line shows
 * "Linked room removed" instead.
 */
export const roomSummary = (
  room: SpecialRequestsRoom,
  lang: string | null | undefined,
  t: SpecialRequestsTranslate
): string => {
  let link: string | null = null;
  if (room.link?.kind === 'room' && room.linkedRoomNo) link = t('linked to Room {n}', { n: room.linkedRoomNo });
  if (room.link?.kind === 'booking') link = t('linked to reservation {n}', { n: room.link.bookingNumber });
  return summarize(
    room.items.map((i) => itemName(i, lang)),
    room.otherWishes,
    link
  );
};

/** R-SR19e: the room is linked to a room that no longer exists. */
export const linkedRoomRemoved = (room: SpecialRequestsRoom): boolean =>
  room.link?.kind === 'room' && !room.linkedRoomNo;

/**
 * The items the popup offers: the active list, plus a retired item the room still carries, ticked, so the agent can untick it
 * (R-SR19g "This item is no longer offered - untick it").
 */
export const popupItems = (
  list: SpecialRequestType[],
  chosen: SpecialRequestItem[] = []
): (SpecialRequestType & { retired: boolean })[] => {
  const active = list.map((t) => ({ ...t, retired: false }));
  const ids = new Set(list.map((t) => t.typeId));
  const retired = chosen
    .filter((c) => !ids.has(c.typeId))
    .map((c) => ({
      typeId: c.typeId,
      nameEn: c.nameEn,
      nameRu: c.nameRu,
      sortOrder: Number.MAX_SAFE_INTEGER,
      retired: true
    }));
  return [...active, ...retired];
};
